import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { translate } from '@ngneat/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, Subject, takeUntil } from 'rxjs';
import { AlertError } from '../../store/actions/shared.actions';
import { AppIntegrationRecord } from '../../models/integrations/appintegrationrecord.class';
import { AppNewIntegrationRecordPayload } from '../../models/integrations/appnewintegrationpayload.class';
import { AuthorizationSystem } from '../../models/integrations/authorization-system.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { getAuthorizationSystem$ } from '../../store/selectors/authorization-systems.selector';
import {
    GetAuthorizationSystems,
    SetAuthorizationSystemId,
    SetAuthorizationSystemName,
    SetAuthorizationSystems,
    SubmitAuthorizationSystem,
} from '../../store/actions/authorizations-system.actions';
import { GetIntegrationsConfig } from '../../store/actions/integrations.actions';
import { getIntegrationsSupportedTypes$ } from '../../store/selectors/integrations.selector';
import { IntegrationModal } from '../../interface/integrationmodal.interface';
import { IntegrationsConfigService } from '../../services/integrations-config/integrations-config.service';
import { IntegrationsUtilsService } from '../../services/integrations-utils/integrations-utils.service';

@Component({
    selector: 'app-edit-authorization-system',
    templateUrl: './edit-authorization-system.component.html',
    styleUrls: ['./edit-authorization-system.component.scss'],
    standalone: false,
})
export class EditAuthorizationSystemComponent {
    @Input() configMeta: IntegrationModal;
    @Input() record: AppIntegrationRecord;
    @Input() drawer: MatDrawer;
    @Input() modal: boolean;
    @Input() closeDrawer: () => void;

    public config: AuthorizationSystem = new AuthorizationSystem();
    public inEdit: boolean;
    private unsubscribe = new Subject<void>();
    public configId: string;

    constructor(
        private dialog: MatDialog,
        private store$: Store,
        private actions$: Actions,
        private _configs: IntegrationsConfigService,
        private _integrationUtils: IntegrationsUtilsService
    ) {}

    public submitConfig = () => {
        if (!this._integrationUtils.isFormValid(this.record as any)) {
            this.store$.dispatch(AlertError({ message: translate('Fix issues to proceed') }));
            return;
        }
        const formData = new FormData();
        const clonedRecord = CommonUtilsService.cloneObject(this.record);
        const payload = new AppNewIntegrationRecordPayload(clonedRecord, this.configMeta, undefined, undefined, undefined);
        const fileFields = payload.files || {};
        delete payload.files;
        formData.append('payload', JSON.stringify(payload));
        Object.keys(fileFields).forEach(fieldId => {
            Object.entries(fileFields[fieldId]).forEach(([_fileName, file]) => {
                formData.append(fieldId, file as Blob);
            });
        });
        this.store$.dispatch(
            SubmitAuthorizationSystem({
                authorizationSystem: formData,
                update: this.inEdit,
            })
        );
    };

    closeModal = () => {
        this.dialog.closeAll();
    };

    initiateSubscriptions = () => {
        this.actions$
            .pipe(
                ofType(SetAuthorizationSystems),
                takeUntil(this.unsubscribe),
                filter(data => data?.updated)
            )
            .subscribe(() => {
                if (this.modal) {
                    this.closeModal();
                    this.store$.dispatch(GetIntegrationsConfig({}));
                } else {
                    this.drawer?.opened && this.closeDrawer();
                }
            });
        this.store$
            .select(getIntegrationsSupportedTypes$)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(supportedTypes => {
                this._integrationUtils.updateSupportedTypes(this.configMeta, supportedTypes);
                this.record?.recordData && this._integrationUtils.updateSupportedTypes(this.record.recordData, supportedTypes);
            });
        this.store$
            .select(getAuthorizationSystem$)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(data => {
                this.record = new AppIntegrationRecord(CommonUtilsService.cloneObject(data), this.configMeta);
                this.inEdit = true;
            });
    };

    ngOnInit(): void {
        if (this.modal) {
            this.configMeta = CommonUtilsService.cloneObject(this._configs.authorizationSystem);
            this.record = new AppIntegrationRecord(undefined, this.configMeta);
            this.store$.dispatch(SetAuthorizationSystemId({ authorizationId: this.configId }));
            this.store$.dispatch(GetAuthorizationSystems());
            this.initiateSubscriptions();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.store$.dispatch(SetAuthorizationSystemName({ name: undefined }));
        this.store$.dispatch(SetAuthorizationSystemId({ authorizationId: undefined }));
    }
}
